import {
  EndOfNextPeriod,
  GetGranularityConfiguration,
  Granularity,
} from "@hydrologiq/common/types/reporting/Granularity"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { IANATimeZone, toTimestamp, startOfDay, endOfDay } from "@hydrologiq/common/helpers/date"
import Reporting, { ReportingSkeleton } from "@hydrologiq/shared/reporting/Reporting"
import { DateTime, days } from "timezonecomplete"
import AssetUsageDownload from "./AssetUsageDownload"
import { useState, useMemo } from "react"
import { FieldSelector } from "@hydrologiq/shared/reporting/FieldSelector"
import { getAvailableFields } from "@hydrologiq/shared/reporting/usageCsv"
import { RowType } from "@hydrologiq/shared/reporting/rows"

export type AssetUsageView = "table"

interface AssetUsageProps {
  start: DateTime
  end: DateTime
  granularity: Granularity
  assetId: string
  assetName: string
  view: AssetUsageView
  latest?: boolean
  title?: boolean
}

export function AssetUsageSkeleton({ view }: { view: AssetUsageView; title?: boolean }) {
  return <ReportingSkeleton />
}

function AssetUsage({ start, end, assetId, assetName, granularity, latest }: AssetUsageProps) {
  const [downloading, setDownloading] = useState<boolean>(false)
  const granularityConfig = GetGranularityConfiguration(granularity)

  let endOfPreview = EndOfNextPeriod(start, granularity, 10)
  if (endOfPreview.greaterThan(end)) endOfPreview = end

  const { loading, data } = useQueryWithEndpoint(
    gqlTypes.AssetUsageDocument,
    {
      variables: {
        id: assetId,
        start: toTimestamp(latest ? startOfDay(end.sub(days(9))) : start, true),
        end: toTimestamp(latest ? endOfDay(end) : endOfPreview, true),
        freq: granularityConfig.queryFrequency,
        location: IANATimeZone(),
      },
    },
    "primary" // Use the primary (experience API) endpoint
  )

  // Get available fields from data
  const availableFields = useMemo(() => {
    const rows = data?.getAssetUsage?.data || []
    return getAvailableFields(rows)
  }, [data])

  // Initialise selected fields with all available fields
  const [selectedFields, setSelectedFields] = useState<(keyof RowType)[]>([])

  // Update selected fields when available fields change (first load)
  useMemo(() => {
    if (availableFields.length > 0 && selectedFields.length === 0) {
      setSelectedFields(availableFields)
    }
  }, [availableFields, selectedFields.length])

  return (
    <Reporting
      start={start}
      end={end}
      granularity={granularity}
      onDataCallback={() => (data?.getAssetUsage?.data ? data.getAssetUsage.data : [])}
      onDownload={() => setDownloading(true)}
      downloading={downloading}
      loading={loading}
      visibleFields={selectedFields}
      fieldSelector={
        availableFields.length > 0 && (
          <FieldSelector
            availableFields={availableFields}
            selectedFields={selectedFields}
            onFieldsChange={setSelectedFields}
            defaultFields={availableFields}
          />
        )
      }
    >
      <AssetUsageDownload
        start={start}
        end={end}
        granularity={granularity}
        asset={assetId}
        assetName={assetName}
        callback={() => setDownloading(false)}
        selectedFields={selectedFields}
      />
    </Reporting>
  )
}

export default AssetUsage
